import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Shuffles an array using the Fisher-Yates algorithm
 */
export function shuffleArray<T>(array: T[]): T[] {
  const shuffled = [...array];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled;
}

/**
 * Generates a deterministic hash from a string (for consistent gradients)
 */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return Math.abs(hash);
}

/**
 * Vivid gradient presets for project cards
 */
const GRADIENT_PRESETS = [
  // Warm tones
  { from: '#FF6B6B', via: '#FFA07A', to: '#FFD93D' },  // Coral to Gold
  { from: '#F093FB', via: '#F5576C', to: '#FFA07A' },  // Pink to Coral
  { from: '#FF9A8B', via: '#FF6A88', to: '#FF99AC' },  // Peachy Pink
  // Cool tones
  { from: '#667EEA', via: '#764BA2', to: '#A855F7' },  // Purple Dream
  { from: '#4FACFE', via: '#00F2FE', to: '#43E97B' },  // Ocean Breeze
  { from: '#0093E9', via: '#80D0C7', to: '#43E97B' },  // Teal Gradient
  { from: '#5433FF', via: '#20BDFF', to: '#A5FECB' },  // Cosmic
  // Vibrant mix
  { from: '#FA709A', via: '#FEE140', to: '#43E97B' },  // Sunset Garden
  { from: '#F7797D', via: '#FBD786', to: '#C6FFDD' },  // Tropical
  { from: '#7F7FD5', via: '#86A8E7', to: '#91EAE4' },  // Lavender Ice
  { from: '#11998E', via: '#38EF7D', to: '#00F260' },  // Mint Fresh
  { from: '#FC466B', via: '#3F5EFB', to: '#11998E' },  // Neon Night
  // Deep tones
  { from: '#834D9B', via: '#D04ED6', to: '#F093FB' },  // Purple Haze
  { from: '#1A2980', via: '#26D0CE', to: '#43E97B' },  // Deep Sea
  { from: '#FF512F', via: '#F09819', to: '#FFD93D' },  // Mango
  { from: '#00B4DB', via: '#0083B0', to: '#667EEA' },  // Blue Lagoon
];

/**
 * Generates a vivid gradient CSS string based on a seed string
 * The gradient is deterministic - same seed always produces same gradient
 */
export function generateVividGradient(seed: string): string {
  const hash = hashString(seed);
  const preset = GRADIENT_PRESETS[hash % GRADIENT_PRESETS.length];
  const angle = (hash % 360);

  return `linear-gradient(${angle}deg, ${preset.from} 0%, ${preset.via} 50%, ${preset.to} 100%)`;
}
